import React, { ComponentProps } from 'react';
import {
    Button,
    ButtonGroup,
    IconButton,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react';
import { faChevronDown, faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FaIcon, ThemeTokens } from '@acp/core-ui';
import { Link } from 'react-router-dom';
import { Tenancy } from '@acp/shared';
import { useTenancy, AddTenancyModal, EditTenancyModal } from '../../../features/tenancy';
import { RequestServiceOnHoldModal, RequestCancellationModal, EditPropertyModal } from '../../../features/property';
import { AccessIssueWizardResolveButton } from '../../../features/access-issues/components/wizard/resolve-button';
import { NewWorkOrderMenuItem } from '../../../features/property-request';

function ActionItems({
    propertyId,
    showCancelProperty,
    showHoldService,
}: {
    propertyId: number;
    showCancelProperty: boolean;
    showHoldService: boolean;
}) {
    const editPropertyKeyModalDisclosure = useDisclosure();
    const putServiceOnHoldModal = useDisclosure();
    const cancellationRequestDisclosure = useDisclosure();
    const editTenancyModalDisclosure = useDisclosure();
    const addTenancyModalDisclosure = useDisclosure();

    const tenanciesQuery = useTenancy(propertyId);

    return (
        <>
            {
                tenanciesQuery.data !== undefined
                && tenanciesQuery.data.active !== null
                && (
                    <MenuItem onClick={editTenancyModalDisclosure.onOpen}>
                        Edit tenancy
                    </MenuItem>
                )
            }
            <MenuItem onClick={addTenancyModalDisclosure.onOpen}>
                Add tenancy
            </MenuItem>
            <MenuItem onClick={editPropertyKeyModalDisclosure.onOpen}>
                Update key number
            </MenuItem>
            <MenuDivider />
            <MenuItem as={Link} to={`/property/${propertyId}`} target="blank">
                View property
                <FaIcon icon={faUpRightFromSquare} ml={2} h={3} />
            </MenuItem>

            <NewWorkOrderMenuItem
                textDecoration="none"
                target="_blank"
                eventGenerator={() => ({
                    type: 'OPEN',
                    propertyId,
                })}
                legacyPath={`/JobOrders/add?property_id=${propertyId}`}
            >
                Request service
            </NewWorkOrderMenuItem>

            {(showHoldService || showCancelProperty) && <MenuDivider />}

            {showHoldService && (
                <MenuItem onClick={putServiceOnHoldModal.onOpen}>Put service on hold</MenuItem>
            )}

            {showCancelProperty && (
                <MenuItem color="red.500" onClick={cancellationRequestDisclosure.onOpen}>
                    Cancel property
                </MenuItem>
            )}

            {showHoldService && (
                <RequestServiceOnHoldModal
                    propertyId={propertyId}
                    isOpen={putServiceOnHoldModal.isOpen}
                    onClose={putServiceOnHoldModal.onClose}
                />
            )}

            {showCancelProperty && (
                <RequestCancellationModal
                    isOpen={cancellationRequestDisclosure.isOpen}
                    onClose={cancellationRequestDisclosure.onClose}
                    propertyId={propertyId}
                />
            )}

            {editPropertyKeyModalDisclosure.isOpen && (
                <EditPropertyModal
                    propertyId={propertyId}
                    isOpen={editPropertyKeyModalDisclosure.isOpen}
                    onClose={editPropertyKeyModalDisclosure.onClose}
                />
            )}

            {editTenancyModalDisclosure.isOpen
                && tenanciesQuery.data !== undefined
                && tenanciesQuery.data.active !== null
                && (
                    <EditTenancyModal
                        tenancy={tenanciesQuery.data.active as Tenancy}
                        propertyId={propertyId}
                        isOpen={editTenancyModalDisclosure.isOpen}
                        onClose={editTenancyModalDisclosure.onClose}
                    />
                )}

            {addTenancyModalDisclosure.isOpen && (
                <AddTenancyModal
                    propertyId={propertyId}
                    isOpen={addTenancyModalDisclosure.isOpen}
                    onClose={addTenancyModalDisclosure.onClose}
                />
            )}
        </>
    );
}

export function OccupantActions({
    propertyId,
    tenancyId,
    title,
    isCustomMenuItems = false,
    children,
    showCancelProperty = false,
    showHoldService = false,
    disabledReason = '',
    hideDropDown = false,
    variant = 'outline',
    useAccessIssueWizard = false,
    ...props
}: {
    propertyId: number;
    tenancyId: number | null;
    title: string;
    disabledReason?: string;
    isCustomMenuItems?: boolean;
    showCancelProperty?: boolean;
    showHoldService?: boolean;
    variant?: string;
    hideDropDown?: boolean;
    useAccessIssueWizard?: boolean;
} & ComponentProps<typeof Button>) {
    const hasChildren = React.Children.count(children) > 0;
    const { isDisabled } = props;
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const isShowAccessIssueWizard = useAccessIssueWizard && !isDisabled;

    return (
        <ButtonGroup
            size="sm"
            isAttached
            justifySelf="end"
        >
            <Tooltip label={disabledReason} placement="top" bg="danger.500" hasArrow>
                {!isShowAccessIssueWizard ? (
                    <Button
                        borderRightWidth={1}
                        borderRightColor="secondary.900"
                        borderColor={ThemeTokens.Defaults.borderColor}
                        color="muted.700"
                        colorScheme="charcoal"
                        variant={variant}
                        {...props}
                    >
                        {title}
                    </Button>
                ) : (
                    <AccessIssueWizardResolveButton propertyId={propertyId} />
                )}
            </Tooltip>
            {hideDropDown === false && (
                <Menu size="large">
                    <MenuButton
                        as={IconButton}
                        borderStartRadius={0}
                        aria-label="Actions Dropdown"
                        icon={<FaIcon icon={faChevronDown} />}
                        borderColor={ThemeTokens.Defaults.borderColor}
                        color="muted.700"
                        colorScheme="charcoal"
                        variant={variant}
                    />
                    <MenuList>
                        {hasChildren && children}
                        {!isCustomMenuItems && (
                            <ActionItems
                                propertyId={propertyId}
                                showCancelProperty={showCancelProperty}
                                showHoldService={showHoldService}
                            />
                        )}
                    </MenuList>
                </Menu>
            )}
        </ButtonGroup>
    );
}
